import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from 'react-i18next';
import {useToast} from '../../modules/layout/components/ToastContext';
import { deleteById, getAll, post } from "../_request";
import {IAxiosGetResponse, IFilterInstitutionModel, IFilterServiceModel, IInstitutionDto, IServiceDto} from "../../interfaces/IUtilities";
import {Link, useNavigate, useParams} from "react-router-dom";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import {AxiosResponse} from "axios";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import {Archive, Box2, FileDiff, FileEarmarkText, Pencil, Trash3} from 'react-bootstrap-icons';
import {Field, Form, Formik} from "formik";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import FiltersCard from "../../modules/layout/components/FiltersCard";
import SortDirection from "../../modules/layout/components/SortDirection";
import TransactionRoutesSearch from "../../modules/layout/components/TransactionRoutesSearch";
import Pagination from "../../modules/layout/components/Pagination";


const ServiceList = () => {
    const {t} = useTranslation();
    const { institutionid } = useParams();
    const {showToast} = useToast();
    const [services, setServices] = useState([] as IServiceDto[]);
    const [institutions, setInstitutions] = useState([] as IInstitutionDto[]);
    const [total, setTotal] = useState(0);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("name");
    const [asc, setAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [isTransactionSearchOpen, setIsTransactionSearchOpen] = useState(false);
 
    const initialFormState = {
        id: '',
        name: '',
        code: '',
        transactionName: '',
        showDeleted: false,
        institutionId: institutionid ?? '',
    }
    let order = asc ? "asc" : "desc";
    const [activeFilters, setActiveFilters] = useState<IFilterServiceModel>(initialFormState);

    const fetchServices = async (filters: IFilterServiceModel = activeFilters) => {
        try {

            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            //if (filters.id) params.push({ key: 'id', value: filters.id });
            if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.name) params.push({ key: 'name', value: filters.name });
            if (filters.code) params.push({ key: 'code', value: filters.code });
            if (filters.transactionName) params.push({ key: 'transactionName', value: filters.transactionName });
            if (filters.showDeleted) params.push({ key: 'showDeleted', value: filters.showDeleted === true ? 1 : 0 });
            if (filters.institutionId || institutionid) params.push({ key: 'institutionId', value: institutionid ?? filters.institutionId });

            let result = await getAll('/api/services/getall/',
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as IServiceDto[];
            setTotal(result?.data.totalCount);

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setServices(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }

    const fetchInstitutions = async () => {
        try {

            let result = await getAll('/api/institutions/gettotal', '');

            let dataResults = result?.data as IInstitutionDto[];

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setInstitutions(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }
    
    useEffect(() => {
        fetchServices();
        fetchInstitutions();
    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const institutionOptions = useMemo(() => (
        (institutions ?? [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((institution) => ({ value: institution.id, label: institution.name }))
    ), [institutions]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const handleDeleteClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowModal(true);
    };

    const handleRecoveryClick = (id: string, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setIsTransactionSearchOpen(true)
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        if (selectedId) {
            try {
                let result = await deleteById("/api/services", selectedId) as AxiosResponse;
                if (result.status === 200) {
                    showToast(t('ServiceDeletedSuccessfully'), 'success');
                    setLoading(false);
                    await fetchServices(activeFilters);
                } else if (result.status === 403) {
                    navigate("/error/403");
                } else if (result.status === 401) {
                    navigate("/auth/login");
                } else if (result.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }

        }
        setShowModal(false);
        setSelectedId(null);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
        setSelectedId(null);
    }

    const handleFilterSearch = async (filters: IFilterServiceModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchServices(filters);
    }

    const changeShowDeteled = async (event: any) => {
        event.preventDefault();
        setActiveFilters({
            ...activeFilters,
            showDeleted: !activeFilters.showDeleted
        })
    }
 
    const handleRecoverSelection = async (ids: string[]) => {
        setLoading(true);
        // ids variable contain the selected transaction route ids from modal
        setIsTransactionSearchOpen(false);
        // Start the restoration request
        if (selectedId) {
            try {
                let result = await post(`/api/services/restore/${selectedId}`, {
                  TransactionRouteIds: ids  
                }) as IAxiosGetResponse;
                if (result?.response.status === 200) {
                    showToast(t('ServiceRestoredSuccessfully'), 'success');
                    setLoading(false);
                    setSelectedId(null);
                    await fetchServices(activeFilters);
                } else if (result.response.status === 403) {
                    navigate("/error/403");
                } else if (result?.response.status === 401) {
                    navigate("/auth/login");
                } else if (result?.response.status === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(result?.response.status)}));
                }
            } catch (error) {
                console.log(t('ConnectionError', {error: String(error)}));
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        }

    }; 

    const closeTransactionSearch = () => { 
        setIsTransactionSearchOpen(false);
    }

    const onErrorTransactionSearch = (error: unknown) => {
        setIsTransactionSearchOpen(false);
        console.log(t('ConnectionError', {error: String(error)})); 
        setLoading(false);
        showToast(t('ConnectionError', {error: String(error)}), 'error');
    }

    return (
        <div className="gg-serviceList container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t('Home'), href: "/" },
                    { title: t('ServiceList') } // Active page (no href)
                ]}/>

            <h3 className="mb-3">{t('ServiceList')}</h3>

            {loading && (<Loader />)}

            <button className="btn btn-success gg-btn-submit mb-3" onClick={() => navigate("/services/create") }>
                {t('Create')}
            </button>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm }) => (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Institution')}
                                        <FormikSelect
                                            name="institutionId"
                                            options={institutionOptions}
                                            isClearable
                                            placeholder={`-- ${t('SelectOption')} --`}
                                            isDisabled={Boolean(institutionid)}
                                        />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Id')}
                                        <Field className="form-control" name="id"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Name')}
                                        <Field className="form-control" name="name"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('Code')}
                                        <Field className="form-control" name="code"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('TransactionName')}
                                        <Field className="form-control" name="transactionName" />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-6 col-xl-4 align-self-end d-flex align-items-center">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState);
                                            }}>
                                        {t('Reset')}
                                    </button>
                                    <label className="form-label w-100 ms-2 small">
                                        <Field type="checkbox" className="form-check-input me-2" name="showDeleted"
                                        checked={activeFilters.showDeleted}
                                        onChange={(e: any) => changeShowDeteled(e)} />
                                        {t('ShowDeleted')}
                                    </label>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                    <tr>
                        <th style={{width: "25%"}} onClick={() => handleSort("name")}>{t('Name')}
                            <SortDirection isActive={sortField === 'name'} order={order}/>
                        </th>
                        <th style={{width: "25%"}} onClick={() => handleSort("code")}>{t('Code')}
                            <SortDirection isActive={sortField === 'code'} order={order}/></th>
                        <th className="text-wrap" style={{width: "30%"}}>{t('Actions')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {services.map(s => (
                        <tr key={s.id}>
                            <td>{s.name}</td>
                            <td>{s.code}</td>
                            <td className="text-wrap">
                                {s.isDeleted ? <>
                                        <button title={t('Restore')} className="btn btn-warning btn-sm me-2"
                                            onClick={() => handleRecoveryClick(s.id, s.name)}>
                                            <Archive size={16} />
                                        </button>
                                </> : <>
                                    <Link title={t('Details')} to={`/services/details/${s.id}`}
                                        className="btn gg-btn btn-sm me-2">
                                        <FileEarmarkText size={16} />
                                    </Link>
                                    <Link title={t('Edit')} to={`/services/edit/${s.id}`} className="btn gg-btn btn-sm me-2">
                                        <Pencil size={16} className="text-danger" />
                                    </Link>
                                        <button title={t('Delete')} className="btn btn-danger btn-sm me-2"
                                            onClick={() => handleDeleteClick(s.id, s.name)}>
                                            <Trash3 size={16} />
                                        </button>
                                    <Link title={t('Transations')} to={`/services/${s.id}/transactions/list`}
                                        className="btn btn-sm btn-warning me-2">
                                        <Box2 size={16} />
                                    </Link>
                                    <Link title={t('DataDifferences')} to={`/services/datadiff/${s.id}/list`}
                                        className="btn gg-btn btn-sm">
                                        <FileDiff size={16} />
                                    </Link>
                                </>}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <ConfirmModal
                show={showModal}
                loading={loading}
                title={t('Delete')}
                message={t('ServiceDeletionConfirmation', {selectedName})}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />

            <Pagination currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                totalItems={total}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size) }} />

            <TransactionRoutesSearch
                serviceid={selectedId}
                isTransactionSearchOpen={isTransactionSearchOpen}
                onSelect={handleRecoverSelection}
                onClose={closeTransactionSearch}
                onError={onErrorTransactionSearch} 
                options={ {
                    title: t('SelectTransactionRoutesForRestoration'),
                    showDeleted: true,
                    multipleSelect: true,
                    canSelectEmptyRoutes: true,
                } } />
        </div>
    );
};

export default ServiceList;
